export type StorageEstimate = {
  supported: boolean;
  usage: number | null;
  quota: number | null;
  remaining: number | null;
  usageRatio: number | null;
};

export type PersistenceStatus = {
  supported: boolean;
  persisted: boolean;
};

export type PdfRetentionAssessment = {
  canLikelyStore: boolean;
  shouldWarn: boolean;
  remainingAfterStore: number | null;
  reason:
    | "enough-space"
    | "near-quota"
    | "exceeds-estimated-quota"
    | "estimate-unavailable";
};

type StorageManagerSubset = Pick<
  StorageManager,
  "estimate" | "persist" | "persisted"
>;

function getStorageManager(): StorageManagerSubset | null {
  if (typeof navigator === "undefined" || navigator.storage === undefined) {
    return null;
  }

  return navigator.storage;
}

export async function estimateStorage(
  storage: StorageManagerSubset | null = getStorageManager(),
): Promise<StorageEstimate> {
  if (!storage?.estimate) {
    return {
      supported: false,
      usage: null,
      quota: null,
      remaining: null,
      usageRatio: null,
    };
  }

  let estimate: Awaited<ReturnType<StorageManagerSubset["estimate"]>>;
  try {
    estimate = await storage.estimate();
  } catch {
    return {
      supported: true,
      usage: null,
      quota: null,
      remaining: null,
      usageRatio: null,
    };
  }
  const usage =
    typeof estimate.usage === "number" ? Math.max(0, estimate.usage) : null;
  const quota =
    typeof estimate.quota === "number" ? Math.max(0, estimate.quota) : null;
  const remaining =
    usage !== null && quota !== null ? Math.max(0, quota - usage) : null;
  const usageRatio =
    usage !== null && quota !== null && quota > 0 ? usage / quota : null;

  return {
    supported: true,
    usage,
    quota,
    remaining,
    usageRatio,
  };
}

export async function requestPersistentStorage(
  storage: StorageManagerSubset | null = getStorageManager(),
): Promise<PersistenceStatus> {
  if (!storage?.persist) {
    return { supported: false, persisted: false };
  }

  try {
    if (storage.persisted && (await storage.persisted())) {
      return { supported: true, persisted: true };
    }

    return { supported: true, persisted: await storage.persist() };
  } catch {
    return { supported: true, persisted: false };
  }
}

export function assessPdfRetention(
  byteLength: number,
  estimate: StorageEstimate,
  reserveBytes = 16 * 1024 * 1024,
): PdfRetentionAssessment {
  if (!Number.isFinite(byteLength) || byteLength < 0) {
    throw new RangeError("PDF byteLength must be a non-negative number.");
  }

  if (estimate.remaining === null) {
    return {
      canLikelyStore: true,
      shouldWarn: false,
      remainingAfterStore: null,
      reason: "estimate-unavailable",
    };
  }

  const remainingAfterStore = estimate.remaining - byteLength;
  if (remainingAfterStore < 0) {
    return {
      canLikelyStore: false,
      shouldWarn: true,
      remainingAfterStore,
      reason: "exceeds-estimated-quota",
    };
  }

  if (remainingAfterStore < reserveBytes) {
    return {
      canLikelyStore: true,
      shouldWarn: true,
      remainingAfterStore,
      reason: "near-quota",
    };
  }

  return {
    canLikelyStore: true,
    shouldWarn: false,
    remainingAfterStore,
    reason: "enough-space",
  };
}

export function canRetainBlob(
  byteLength: number,
  estimate: StorageEstimate,
): boolean {
  return assessPdfRetention(byteLength, estimate).canLikelyStore;
}
